/**
 * centerFittingsLogic.js
 * 
 * Модуль для работы с центральными фитингами (термометры и лицевые подключения)
 * на основной гребенке коллектора.
 */

import { facingStickFields, getFittingConnectionDiameter, getHoleDiameterForPipe } from './collectorUtils';

import fittingsData from '../fittingsData';

/**
 * Генерирует центральные фитинги на гребенке
 * 
 * @param {Object} config - Конфигурация для генерации
 * @param {Array} config.centerFittings - Массив центральных фитингов
 * @param {string} config.mainPipeId - ID основной трубы
 * @param {number} config.mainPipeDiameter - Диаметр основной трубы
 * @param {number} config.mainCenterY - Y-координата центра основной трубы
 * @param {number} config.startX - X-координата начала (включая заглушки)
 * @param {number} config.mainPipeStartX - X-координата начала основной трубы (без заглушек)
 * @param {number} config.mainPipeLength - Длина основной трубы (включая заглушки)
 * @param {number} config.actualMainPipeLength - Длина основной трубы (без заглушек)
 * @param {Array} config.circuits - Массив контуров для расчета "между контурами"
 * @param {Array} config.bottomCircuits - Массив нижних контуров (для этажного коллектора)
 * @param {number} config.circuitSpacing - Расстояние между контурами
 * @param {number} config.firstCircuitOffset - Отступ первого контура
 * @param {number} config.bottomFirstCircuitOffset - Отступ первого нижнего контура (для этажного коллектора)
 * @param {number} config.bottomCircuitSpacing - Расстояние между нижними контурами (для этажного коллектора)
 * @param {string} config.collectorType - Тип коллектора: 'supply' (подача) или 'return' (обратка) - для распределительного коллектора
 * @param {number} config.leftStraightCapLength - Длина левой прямой заглушки
 * 
 * @returns {Object} - Объект с массивами fittings, welds, centerFittingObjects
 */
export function generateCenterFittings(config) {
  const {
    centerFittings = [],
    mainPipeId,
    mainCenterY,
    startX,
    mainPipeStartX,
    mainPipeLength,
    circuits = [],
    bottomCircuits = [],
    circuitSpacing = 0,
    firstCircuitOffset = 0,
    bottomFirstCircuitOffset = 0,
    bottomCircuitSpacing = 0,
    collectorType = 'supply', // По умолчанию подача
    shape = 'round', // 'round' или 'square'
  } = config;

  const fittings = [];
  const welds = [];
  const centerFittingObjects = [];

  if (!centerFittings || centerFittings.length === 0) {
    return { fittings, welds, centerFittingObjects };
  }

  centerFittings.forEach((fitting, index) => {
    const fittingInfo = fittingsData[fitting.fittingId];
    
    if (!fittingInfo) {
      console.warn(`Фитинг ${fitting.fittingId} не найден в базе данных`);
      return;
    }

    // Визуальные размеры для selfFacing
    const selfFacingInfo = fittingInfo.selfFacing || {};
    const fDiameter = selfFacingInfo.diameter || fittingInfo.diameter;
    const fLength = selfFacingInfo.length || fittingInfo.diameter;
    // Для отверстия используем реальный диаметр фитинга, а не визуальный selfFacing
    const holePipeDiameter = getFittingConnectionDiameter(fitting.fittingId) || fittingInfo.diameter;

    const positionType = fitting.positionType || 'center';
    const side = fitting.side || 'both';
    
    // Расчет X-позиции в зависимости от типа размещения
    let fittingX = startX;
    
    switch (positionType) {
      case 'distance': {
        // На заданном расстоянии от начала или конца (включая заглушки)
        const distance = Number(fitting.distanceFromStart) || 50;
        const direction = fitting.distanceDirection || 'left'; // 'left' (слева) или 'right' (справа)
        
        if (direction === 'right') {
          // Отсчёт справа налево
          fittingX = startX + mainPipeLength - distance;
        } else {
          // Отсчёт слева направо (по умолчанию)
          fittingX = startX + distance;
        }
        break;
      }
      
      case 'center': {
        // По середине гребенки (включая заглушки)
        fittingX = startX + mainPipeLength / 2;
        break;
      }
      
      case 'between':
      case 'betweenCircuits':
      case 'betweenFittings': {
        // Между контурами или между патрубками
        // betweenIndex - это индекс (0, 1, 2...), который обозначает:
        // для между контурами: 0 = между 1 и 2, 1 = между 2 и 3 и т.д.
        const betweenIndex = Number(fitting.betweenIndex) ?? 0;
        
        // Определяем, какой массив использовать для проверки структуры
        // Если circuits пустой, проверяем bottomCircuits
        const checkArray = circuits.length > 0 ? circuits : bottomCircuits;
        
        // Если circuits или bottomCircuits - это массив объектов с spacingToNext (этажный или распределительный коллектор)
        if (checkArray.length > 0 && typeof checkArray[0] === 'object' && checkArray[0].spacingToNext !== undefined) {
          // Проверяем есть ли circuitSpacing (распределительный) или нет (этажный)
          const isDistributionCollector = checkArray[0].circuitSpacing !== undefined;
          
          if (isDistributionCollector) {
            // РАСПРЕДЕЛИТЕЛЬНЫЙ КОЛЛЕКТОР: у каждого контура 2 патрубка
            // Определяем, какую линию (подающую или обратную) использовать для расчёта
            const referenceRow = fitting.referenceRow || 'supply';
            
            // Для подающего коллектора: circuits=подача, bottomCircuits=обратка
            // Для обратного коллектора: circuits=обратка, bottomCircuits=подача
            // Поэтому логика выбора зависит от типа текущего коллектора
            const useAlternate = (collectorType === 'supply' && referenceRow === 'return') || 
                                 (collectorType === 'return' && referenceRow === 'supply');
            
            const selectedCircuits = useAlternate ? bottomCircuits : circuits;
            const selectedFirstOffset = useAlternate ? bottomFirstCircuitOffset : firstCircuitOffset;
            const selectedSpacing = useAlternate ? bottomCircuitSpacing : circuitSpacing;
            
            // Проверяем минимальное количество контуров в зависимости от типа размещения
            const minRequiredCircuits = positionType === 'betweenCircuits' ? 2 : 1;
            if (!selectedCircuits || selectedCircuits.length < minRequiredCircuits) {
              // Если выбранных контуров недостаточно, используем центр
              const lineName = referenceRow === 'return' ? 'обратной' : 'подающей';
              console.warn(`Недостаточно контуров на ${lineName} линии для размещения центрального фитинга "${positionType === 'betweenCircuits' ? 'между контурами' : 'между патрубками'}". Используется центр гребенки.`);
              fittingX = startX + mainPipeLength / 2;
            } else {
              const circuitPositions = [];
              let currentX = selectedFirstOffset;
              selectedCircuits.forEach((circuit, i) => {
                const internalSpacing = Number(circuit.circuitSpacing) || 50;
                circuitPositions.push({
                  start: mainPipeStartX + currentX,
                  spacing: internalSpacing
                });
                
                // Переход к следующему контуру
                if (i < selectedCircuits.length - 1) {
                  const spacingToNext = Number(circuit.spacingToNext) || selectedSpacing;
                  currentX += internalSpacing + spacingToNext;
                }
              });
              
              // Логика для распределительного коллектора
              if (positionType === 'betweenCircuits') {
                // Между контурами
                const circuit1Index = betweenIndex;
                const circuit2Index = betweenIndex + 1;
                
                if (circuit1Index < circuitPositions.length && circuit2Index < circuitPositions.length) {
                  const pos1End = circuitPositions[circuit1Index].start + circuitPositions[circuit1Index].spacing;
                  const pos2Start = circuitPositions[circuit2Index].start;
                  fittingX = (pos1End + pos2Start) / 2;
                } else {
                  // Если индекс выходит за границы, используем центр
                  console.warn(`Индекс betweenIndex (${betweenIndex}) выходит за границы массива контуров (длина ${circuitPositions.length}). Используется центр гребенки.`);
                  fittingX = startX + mainPipeLength / 2;
                }
              } else {
                // Между патрубками (betweenFittings или between)
                const allFittingPositions = [];
                circuitPositions.forEach(pos => {
                  allFittingPositions.push(pos.start);
                  allFittingPositions.push(pos.start + pos.spacing);
                });
                
                if (betweenIndex < allFittingPositions.length - 1) {
                  const x1 = allFittingPositions[betweenIndex];
                  const x2 = allFittingPositions[betweenIndex + 1];
                  fittingX = (x1 + x2) / 2;
                } else {
                  // Если индекс выходит за границы, используем центр
                  console.warn(`Индекс betweenIndex (${betweenIndex}) выходит за границы массива патрубков (длина ${allFittingPositions.length}). Используется центр гребенки.`);
                  fittingX = startX + mainPipeLength / 2;
                }
              }
            }
          } else {
            // ЭТАЖНЫЙ КОЛЛЕКТОР: у каждого контура 1 патрубок
            // Определяем, какую трубу (верхнюю или нижнюю) использовать для расчёта
            const referenceRow = fitting.referenceRow || 'top';
            const selectedCircuits = referenceRow === 'bottom' ? bottomCircuits : circuits;
            const selectedFirstOffset = referenceRow === 'bottom' ? bottomFirstCircuitOffset : firstCircuitOffset;
            const selectedSpacing = referenceRow === 'bottom' ? bottomCircuitSpacing : circuitSpacing;
            
            // Проверяем, что выбранный массив контуров не пустой и имеет достаточно элементов
            if (!selectedCircuits || selectedCircuits.length < 2) {
              // Если выбранных контуров недостаточно, используем центр
              console.warn(`Недостаточно ${referenceRow === 'bottom' ? 'нижних' : 'верхних'} контуров для размещения центрального фитинга "между контурами". Используется центр гребенки.`);
              fittingX = startX + mainPipeLength / 2;
            } else {
              // Вычисляем позиции контуров с учетом индивидуальных расстояний
              const circuitPositions = [];
              let currentX = selectedFirstOffset;
              selectedCircuits.forEach((circuit, i) => {
                circuitPositions.push(mainPipeStartX + currentX);
                
                // Переход к следующему контуру
                if (i < selectedCircuits.length - 1) {
                  const spacingToNext = Number(circuit.spacingToNext) || selectedSpacing;
                  currentX += spacingToNext;
                }
              });
              
              // Для этажного коллектора есть только "между контурами" (between)
              const x1 = circuitPositions[betweenIndex];
              const x2 = circuitPositions[betweenIndex + 1];
              
              if (x1 !== undefined && x2 !== undefined) {
                fittingX = (x1 + x2) / 2;
              } else {
                // Если индекс выходит за границы, используем центр
                console.warn(`Индекс betweenIndex (${betweenIndex}) выходит за границы массива контуров (длина ${circuitPositions.length}). Используется центр гребенки.`);
                fittingX = startX + mainPipeLength / 2;
              }
            }
          }
        } else {
          // Старая логика для простых массивов или этажного коллектора
          const first = betweenIndex + 1;
          const second = betweenIndex + 2;
          
          if (circuits.length >= second) {
            const firstCircuitX = mainPipeStartX + firstCircuitOffset;
            
            if (positionType === 'betweenCircuits') {
              const circuit1CenterIndex = first * 2 - 1;
              const circuit2CenterIndex = second * 2 - 1;
              const circuit1X = firstCircuitX + (circuit1CenterIndex - 0.5) * circuitSpacing;
              const circuit2X = firstCircuitX + (circuit2CenterIndex - 0.5) * circuitSpacing;
              fittingX = (circuit1X + circuit2X) / 2;
            } else {
              const circuit1X = firstCircuitX + (first - 1) * circuitSpacing;
              const circuit2X = firstCircuitX + (second - 1) * circuitSpacing;
              fittingX = (circuit1X + circuit2X) / 2;
            }
          } else {
            fittingX = startX + mainPipeLength / 2;
          }
        }
        break;
      }
      
      default:
        fittingX = startX + mainPipeLength / 2;
    }

    const fittingId = `center-fitting-${mainPipeId}-${index + 1}`;
    const fittingCount = side === 'both' ? 2 : 1;
    const fittingIds = [];
    
    // Создаем фитинги (1 или 2 в зависимости от стороны)
    for (let i = 0; i < fittingCount; i++) {
      const currentFittingId = fittingCount > 1 ? `${fittingId}-${i + 1}` : fittingId;
      fittingIds.push(currentFittingId);
      
      // Определяем сторону для каждого фитинга
      let fittingSide = side;
      if (side === 'both') {
        // Первый фитинг - спереди, второй - сзади
        fittingSide = i === 0 ? 'front' : 'back';
      }
      
      fittings.push({
        id: currentFittingId,
        key: fitting.fittingId,
        itemKey: fitting.fittingId,
        x: 0,
        y: 0,
        width: fDiameter,
        height: fLength,
        groupX: fittingX,
        groupY: mainCenterY,
        rotation: 0,
        isSelfFacing: true, // Фитинг направлен "на себя"
        isCenterFitting: true, // Маркер центрального фитинга для исключения сварок между собой
        side: fittingSide, // Сторона: 'front', 'back'
        offsetX: fDiameter / 2, // Центр по X
        offsetY: fLength / 2, // Центр по Y (середина фитинга = середина гребенки)
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        igesCreateHole: true, // IGES: создаёт отверстие
        holeDiameter: getHoleDiameterForPipe(holePipeDiameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (реальный диаметр фитинга, особая логика для муфты 1/2" на квадратных)
        igesHoleDirection: fittingSide, // IGES: направление отверстия ('front' или 'back')
        ...facingStickFields(fitting),
      });
      
      // Сварка фитинга с гребенкой
      welds.push({
        id: `weld-center-${index + 1}-${i + 1}-${Date.now()}`,
        x: fittingX,
        y: mainCenterY,
        objects: [mainPipeId, currentFittingId],
        isCenterFittingWeld: true, // Маркер для исключения из расчёта сварок между центральными фитингами
      });
    }
    
    // Сохраняем информацию о фитинге для размерных линий и позиций
    centerFittingObjects.push({
      id: fittingIds[0], // Используем ID первого фитинга для привязки
      fittingIds: fittingIds, // Все ID для balloons
      x: fittingX,
      y: mainCenterY,
      positionType: positionType,
      distanceFromStart: positionType === 'distance' ? (Number(fitting.distanceFromStart) || 50) : null,
      distanceDirection: positionType === 'distance' ? (fitting.distanceDirection || 'left') : null,
      fittingKey: fitting.fittingId, // Сохраняем ключ фитинга для позиции
      side: side, // Сохраняем сторону для balloon
    });
  });

  return { fittings, welds, centerFittingObjects };
}

/**
 * Генерирует размерные линии для центральных фитингов с positionType === 'distance'
 * 
 * @param {Array} centerFittingObjects - Массив объектов центральных фитингов
 * @param {string} leftCapId - ID левой заглушки для привязки
 * @param {string} rightCapId - ID правой заглушки для привязки (для направления справа)
 * @param {number} startX - X-координата начала коллектора
 * @param {number} mainCenterY - Y-координата центра основной трубы
 * @param {number} mainPipeDiameter - Диаметр основной трубы
 * @param {number} mainPipeLength - Полная длина коллектора (для расчёта справа)
 * @param {number} leftStraightCapLength - Длина левой прямой заглушки
 * @param {number} rightStraightCapLength - Длина правой прямой заглушки
 * @param {string} mainPipeId - ID основной трубы (для привязки если нет заглушек)
 * @param {number} actualMainPipeLength - Фактическая длина трубы без заглушек
 * @param {number} mainPipeStartX - X-координата начала основной трубы (без заглушек)
 * @param {Object} leftConnectionInfo - Информация о левом торцевом подключении (с bortshaibaId)
 * @param {Object} rightConnectionInfo - Информация о правом торцевом подключении (с bortshaibaId)
 * @param {number} leftBortshaibaLength - Длина левой бортшайбы
 * @param {number} rightBortshaibaLength - Длина правой бортшайбы
 * 
 * @returns {Array} - Массив размерных линий
 */
export function generateCenterFittingDimensions(
  centerFittingObjects,
  leftCapId,
  rightCapId,
  startX,
  mainCenterY,
  mainPipeDiameter,
  mainPipeLength,
  leftStraightCapLength = 0,
  rightStraightCapLength = 0,
  mainPipeId = null,
  actualMainPipeLength = 0,
  mainPipeStartX = null,
  leftConnectionInfo = null,
  rightConnectionInfo = null,
  leftBortshaibaLength = 0,
  rightBortshaibaLength = 0
) {
  const dimensionLines = [];
  const dimY = mainCenterY; // На середине гребенки

  centerFittingObjects.forEach((centerFitting) => {
    if (centerFitting.positionType === 'distance' && centerFitting.distanceFromStart != null) {
      const displayValue = centerFitting.distanceFromStart;
      const direction = centerFitting.distanceDirection || 'left';
      
      if (direction === 'right') {
        // Отсчёт справа - размерная линия от фитинга до правой заглушки/бортшайбы/трубы
        const rightEdgeX = startX + mainPipeLength;
        
        // Приоритет привязки: прямая заглушка > бортшайба > труба
        let rightElement;
        if (rightCapId && rightStraightCapLength > 0) {
          rightElement = { objectId: rightCapId, objectType: 'fitting', x: rightEdgeX - rightStraightCapLength, y: mainCenterY, type: 'top' };
        } else if (rightConnectionInfo?.bortshaibaId && rightBortshaibaLength > 0) {
          rightElement = { objectId: rightConnectionInfo.bortshaibaId, objectType: 'fitting', x: rightEdgeX, y: mainCenterY, type: 'top' };
        } else {
          rightElement = { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX + actualMainPipeLength, y: mainCenterY, type: 'right' };
        }
        
        dimensionLines.push({
          id: `dim-center-${centerFitting.id}-${Date.now()}`,
          isHorizontal: true,
          lockOrientation: true,
          angle: 0,
          x1: centerFitting.x,
          y1: mainCenterY,
          x2: rightEdgeX,
          y2: mainCenterY,
          position: { x: 0, y: dimY },
          value: String(Math.round(displayValue)),
          rawValue: displayValue,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: [
            { objectId: centerFitting.id, objectType: 'fitting', x: centerFitting.x, y: mainCenterY, type: 'top' },
            rightElement,
          ],
        });
      } else {
        // Отсчёт слева - размерная линия от левой заглушки/бортшайбы/трубы до фитинга
        
        // Приоритет привязки: прямая заглушка > бортшайба > труба
        let leftElement;
        if (leftCapId && leftStraightCapLength > 0) {
          leftElement = { objectId: leftCapId, objectType: 'fitting', x: startX + leftStraightCapLength, y: mainCenterY, type: 'top' };
        } else if (leftConnectionInfo?.bortshaibaId && leftBortshaibaLength > 0) {
          leftElement = { objectId: leftConnectionInfo.bortshaibaId, objectType: 'fitting', x: startX, y: mainCenterY, type: 'top' };
        } else {
          leftElement = { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX, y: mainCenterY, type: 'left' };
        }
        
        dimensionLines.push({
          id: `dim-center-${centerFitting.id}-${Date.now()}`,
          isHorizontal: true,
          lockOrientation: true,
          angle: 0,
          x1: startX,
          y1: mainCenterY,
          x2: centerFitting.x,
          y2: mainCenterY,
          position: { x: 0, y: dimY },
          value: String(Math.round(displayValue)),
          rawValue: displayValue,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: [
            leftElement,
            { objectId: centerFitting.id, objectType: 'fitting', x: centerFitting.x, y: mainCenterY, type: 'top' },
          ],
        });
      }
    }
  });

  return dimensionLines;
}

/**
 * Генерирует позиции (balloons) для центральных фитингов
 * 
 * @param {Array} centerFittingObjects - Массив объектов центральных фитингов
 * @param {Function} getFittingLabel - Функция для получения метки фитинга
 * @param {number} midCollectorY - Y-координата середины коллектора
 * @param {number} extraXOffset - Дополнительное смещение по X (для избежания наложения в распределительном коллекторе)
 * @param {string} idPrefix - Префикс для ID (для уникальности в распределительном коллекторе)
 * 
 * @returns {Array} - Массив позиций (balloons)
 */
export function generateCenterFittingBalloons(
  centerFittingObjects,
  getFittingLabel,
  midCollectorY,
  extraXOffset = 0,
  idPrefix = ''
) {
  const balloons = [];

  centerFittingObjects.forEach((centerFitting, index) => {
    let label = getFittingLabel(centerFitting.fittingKey);
    
    // Добавляем модификатор текста в зависимости от стороны
    if (centerFitting.side === 'both') {
      label = label + '\n(c 2-х ст.)';
    } else if (centerFitting.side === 'back') {
      label = label + '\n(сзади)';
    }
    // Для 'front' ничего не добавляем
    
    // Для "с 2-х сторон" сдвигаем позицию дальше из-за визуального перекрытия фитингов
    const xOffset = centerFitting.side === 'both' ? 65 : 50;
    
    // Определяем targets в зависимости от количества фитингов
    const targets = centerFitting.fittingIds.map(id => ({ type: 'fitting', id: id }));
    
    balloons.push({
      id: `balloon-center-${idPrefix}${index + 1}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      x: centerFitting.x + xOffset + extraXOffset, // Справа от фитинга + дополнительное смещение
      y: midCollectorY,
      text: label,
      textAlign: 'center',
      targets: targets,
    });
  });

  return balloons;
}

/**
 * Обновляет центральные фитинги при изменении диаметра основной трубы,
 * переключая несовместимые фитинги на максимальный совместимый в той же категории
 * 
 * @param {Array} centerFittings - Текущий массив центральных фитингов
 * @param {number} newDiameter - Новый диаметр основной трубы
 * @param {Function} isFittingCompatibleWithMainPipe - Функция проверки совместимости
 * @param {Function} getFittingsByCategory - Функция получения фитингов по категории
 * 
 * @returns {Array} - Обновленный массив центральных фитингов
 */
export function updateCenterFittingsForDiameter(
  centerFittings,
  newDiameter,
  isFittingCompatibleWithMainPipe,
  getFittingsByCategory
) {
  return centerFittings.map((fitting) => {
    if (!isFittingCompatibleWithMainPipe(fitting.fittingId, newDiameter)) {
      // Фитинг несовместим - пытаемся найти совместимый в той же категории
      const fittingsInCategory = getFittingsByCategory(fitting.fittingCategory || 'Муфты');
      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
      
      if (compatibleFittings.length > 0) {
        // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
        return {
          ...fitting,
          fittingId: maxCompatibleFitting.id,
        };
      } else {
        // Нет совместимых фитингов в категории - сбрасываем на дефолтные значения
        // Проверяем, совместима ли дефолтная Mufta_1.2
        const defaultFittingId = 'Mufta_1.2';
        if (isFittingCompatibleWithMainPipe(defaultFittingId, newDiameter)) {
          return {
            ...fitting,
            fittingCategory: 'Муфты',
            fittingId: defaultFittingId,
          };
        } else {
          // Если даже дефолтная не совместима, ищем максимальную совместимую муфту
          const muftaFittings = getFittingsByCategory('Муфты');
          const compatibleMuftas = muftaFittings.filter(f => f.isCompatible);
          
          if (compatibleMuftas.length > 0) {
            const maxCompatibleMufta = compatibleMuftas[compatibleMuftas.length - 1];
            return {
              ...fitting,
              fittingCategory: 'Муфты',
              fittingId: maxCompatibleMufta.id,
            };
          } else {
            // Совсем нет совместимых - оставляем как есть (будет ошибка при генерации)
            return fitting;
          }
        }
      }
    }
    return fitting;
  });
}

